<template>
  <div class="fu-drm" data-test="fu-drawer-methods">
    <div class="fu-drm__grid" role="group" aria-label="How did you follow up?">
      <button
        v-for="m in tiles"
        :key="m.key"
        type="button"
        class="fu-drm__tile"
        :class="{ 'fu-drm__tile--on': m.key === active && m.enabled, 'fu-drm__tile--off': !m.enabled }"
        :aria-pressed="m.enabled ? String(m.key === active) : null"
        :aria-disabled="m.enabled ? null : 'true'"
        :aria-expanded="m.enabled ? null : String(explained === m.key)"
        :data-test="`fu-drawer-method-${m.key}`"
        :data-state="m.enabled ? (m.key === active ? 'on' : 'off') : 'disabled'"
        @mousedown="m.enabled || $event.preventDefault()"
        @click="onTile(m)"
      >
        <fu-icon :name="m.icon" :size="20" />
        <span class="fu-drm__label">{{ m.label }}<span v-if="m.key === 'ai'" class="fu-drm__sim">SIM</span></span>
        <span v-if="!m.enabled && m.why" class="fu-drm__why">{{ m.why }}</span>
      </button>
    </div>

    <div v-if="explainedTile" class="fu-drm__reasons" role="region" :aria-label="`Why ${explainedTile.label} is off`" data-test="fu-drawer-method-reasons">
      <div class="fu-drm__reasons-title">{{ explainedTile.key === 'payment' ? 'Payment link is off because:' : `Why is ${explainedTile.label} off?` }}</div>
      <ul class="fu-drm__reasons-list">
        <li
          v-for="(r, i) in explainedTile.reasonList"
          :key="i"
          :class="{ 'fu-drm__reason--feature': r.isFeatureIssue }"
        >{{ r.text }}</li>
      </ul>

      <div v-if="explainedTile.upsell" class="fu-drm__upsell" :data-test="`fu-drawer-upsell-${explainedTile.key}`">
        <p class="fu-drm__upsell-copy"><strong>{{ upsellCopy(explainedTile.key).title }}</strong> — {{ upsellCopy(explainedTile.key).body }}</p>
        <slot name="upsell" :upsell="explainedTile.upsell" :method="explainedTile.key" />
      </div>
      <p v-else-if="explainedTile.showTip" class="fu-drm__tip"><strong>Tip:</strong> {{ tipOf(explainedTile.key) }}</p>
    </div>
  </div>
</template>

<script>
/**
 * DrawerMethodPicker — Phone · Text · Email · Payment link · AI call (the design's method tiles),
 * each on / off / disabled exactly as drawerMethods decides.
 *
 *   methods  drawerMethods.resolveDrawerMethods() — only `shown` ones render; AI's state is its
 *            `buttonDisabled` (in progress / no mobile), every other tile's is `enabled`
 *   active   the open method's key, or null
 *
 * A disabled tile is still a focusable <button aria-disabled> (from the keyboard; a mouse press
 * does not focus it, and it never wears the selected tile's teal ring): pressing it opens the WHY panel —
 * the rules' reasons (today's popover text), and when the PRACTICE lacks the feature the
 * request-to-enable upsell with today's copy. The upsell's button is the container's
 * <FeatureRequestButton feature-key="SMS|EMAIL"> in the `upsell` slot ({ upsell, method }) — it
 * reads the store, and feature-request-paid-only-guardrail wants its feature-key literal in the file
 * that renders it.
 * Events: select(key) for an enabled tile (the container still checks opensForm / clickError).
 * Presentational: no store, no mixin.
 */
import FuIcon from '../FuIcon.vue';
import { METHOD_LABELS, shortReason } from '@/utils/followups/drawer/drawerMethods.js';

const ICONS = { phone: 'phone', sms: 'text', email: 'mail', payment: 'dollar', ai: 'bot' };

// Today's popover copy (CreateFollowup.vue:162, :208, :174, :220).
const UPSELL_COPY = {
  sms: { title: 'Enable SMS Followups', body: 'send automated SMS follow-ups to patients for recalls, treatment, and payment collection.' },
  email: { title: 'Enable Email Followups', body: 'send professional email follow-ups to patients with customizable templates and tracking.' },
};
const TIPS = {
  sms: 'Update patient contact information or check follow-up type restrictions.',
  email: 'Update patient email address or check follow-up type restrictions.',
};

export default {
  name: 'DrawerMethodPicker',
  components: { FuIcon },
  props: {
    methods: { type: Array, default: () => [] },
    active: { type: String, default: null },
  },
  data() {
    return { explained: null };
  },
  computed: {
    tiles() {
      return this.methods.filter(m => m && m.shown).map((m) => {
        const enabled = m.key === 'ai' ? !m.buttonDisabled : !!m.enabled;
        const reasonList = (m.reasons || []).map(r => (typeof r === 'object' ? r : { text: r, isFeatureIssue: false }));
        let why = enabled ? '' : shortReason(Object.assign({}, m, { enabled }));
        if (m.key === 'ai' && !enabled) {
          // aiCallMethod: `enabled` = a wireless number; buttonDisabled adds "a call is in progress"
          why = m.enabled ? 'Calling…' : 'No mobile number';
          reasonList.push({ text: m.enabled ? 'An AI call is already in progress.' : 'The patient has no mobile number on file.', isFeatureIssue: false });
        }
        return {
          key: m.key,
          label: METHOD_LABELS[m.key] || m.key,
          icon: ICONS[m.key] || 'info',
          enabled,
          why,
          reasonList,
          upsell: m.upsell || null,
          showTip: !!m.showTip,
        };
      });
    },
    explainedTile() {
      return this.explained ? this.tiles.find(t => t.key === this.explained && !t.enabled) || null : null;
    },
  },
  methods: {
    onTile(m) {
      if (!m.enabled) {
        this.explained = this.explained === m.key ? null : m.key;
        return;
      }
      this.explained = null;
      this.$emit('select', m.key);
    },
    upsellCopy(key) { return UPSELL_COPY[key] || { title: '', body: '' }; },
    tipOf(key) { return TIPS[key] || ''; },
  },
};
</script>

<style lang="scss" scoped>
@import './drawer';

.fu-drm__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(92px, 1fr));
  gap: 8px;
}

.fu-drm__tile {
  @include fu-button-reset;
  @include fu-focus-ring;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 5px;
  min-height: 62px;
  padding: 6px;
  border-radius: 11px;
  border: 1px solid $fu-border;
  background: $fu-surface;
  color: $fu-neutral-600;
  font-size: 12.5px;
  font-weight: $fu-fw-bold;
  text-align: center;
  transition: background $fu-dur-fast $fu-ease-out, color $fu-dur-fast $fu-ease-out;
  &:hover { background: $fu-neutral-50; color: $fu-text; }
}

.fu-drm__tile--on {
  border-color: $fu-brand;
  background: $fu-brand-50;
  color: $fu-brand-800;
  box-shadow: $fu-ring;
  &:hover { background: $fu-brand-50; color: $fu-brand-800; }
}

.fu-drm__tile--off {
  gap: 3px;
  border-style: dashed;
  border-color: $fu-border-strong;
  background: $fu-neutral-25;
  color: $fu-text-subtle;
  cursor: help;
  &:hover { background: $fu-neutral-50; color: $fu-text-muted; }
  // A disabled tile must never look selected: the teal ring IS the selected look (--on), so a
  // focused disabled tile gets no ring after a click and only a grey dashed outline from the
  // keyboard.
  &:focus,
  &:focus-visible { box-shadow: none; }
  &:focus-visible { outline: 2px dashed $fu-neutral-400; outline-offset: 2px; }
}

.fu-drm__label { display: inline-flex; align-items: center; gap: 5px; white-space: nowrap; }

.fu-drm__sim {
  display: inline-flex;
  align-items: center;
  height: 15px;
  padding: 0 5px;
  border-radius: 5px;
  background: $fu-cat-claim-subtle;
  color: $fu-cat-claim;
  font-size: 9.5px;
  font-weight: 800;
}

.fu-drm__why { font-size: 10.5px; font-weight: $fu-fw-semibold; line-height: 1.2; }

.fu-drm__reasons {
  margin-top: 10px;
  padding: 12px 14px;
  border-radius: 11px;
  border: 1px solid $fu-border;
  background: $fu-neutral-25;
  font-size: 12.5px;
  line-height: 1.45;
  color: $fu-neutral-600;
}

.fu-drm__reasons-title { font-weight: $fu-fw-bold; color: $fu-text; }
.fu-drm__reasons-list { margin: 6px 0 0; padding-left: 18px; }
.fu-drm__reason--feature { color: $fu-warning-text; }

.fu-drm__upsell {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid $fu-border;
}

.fu-drm__upsell-copy { margin: 0 0 8px; }
.fu-drm__tip { margin: 8px 0 0; }
</style>
